import { MediaFrame, SplitReveal, TiltCard } from '@/components/motion';
import type { ProgrammeGalleryItem, ProgrammeType } from '@/data/programmes';
import { cn } from '@ibas/ui';

export interface ProgrammeGalleryProps {
  /** Real IBAS photo tiles (never the page hero asset). Needs ≥3 to mount. */
  items: readonly ProgrammeGalleryItem[];
  /** Delivery type — switches the eyebrow/heading between campus and community. */
  type: ProgrammeType;
  className?: string;
  /** Optional id for in-page anchoring. */
  id?: string;
}

/**
 * ProgrammeGallery — a campus/community photo bento built from programme.gallery.
 *
 * An images-only lookbook of MediaFrame tiles: each curtain-wipes in on scroll
 * (MediaFrame's ImageReveal) and, on pointer devices, leans toward the cursor
 * inside a TiltCard with a soft gold glare. On hover a caption rises behind a
 * gold hairline over a navy scrim. On-site programmes read as "Campus & student
 * life"; online programmes (the Flexi-DBA) as "Our community", so scene captions
 * never imply a fully-online student attends scheduled on-campus classes.
 *
 * Reduced motion / touch: TiltCard renders flat, reveals are disabled and every
 * caption is permanently visible — a complete, static composition.
 *
 * Self-hides below three tiles so the grid never renders thin or broken.
 */
export function ProgrammeGallery({ items, type, className, id }: ProgrammeGalleryProps) {
  if (items.length < 3) return null;

  const onSite = type === 'On-site';
  const eyebrow = onSite ? 'Campus & student life' : 'Our community';
  const heading = onSite ? 'Life at Campus Thalwil.' : 'A global learning community.';

  // Four tiles read as an editorial single row on desktop; three sit cleanly in a
  // 3-up. Portrait tiles give the bento a lookbook rhythm without cropping people.
  const cols = items.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';

  return (
    <section id={id} className={cn('bg-background py-16 md:py-24', className)}>
      <div className="mx-auto w-full max-w-6xl px-6">
        <header className="mb-8 max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            {eyebrow}
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            {heading}
          </SplitReveal>
        </header>

        <div className={cn('grid grid-cols-2 gap-3 md:gap-4', cols)}>
          {items.map((tile) => (
            <div key={tile.image} data-cursor className="group relative">
              <TiltCard className="h-full" max={6} glare>
                <MediaFrame
                  image={tile.image}
                  alt={tile.alt}
                  aspect="aspect-[4/5]"
                  className="h-full"
                  position={tile.position ?? 'center'}
                  overlay={14}
                >
                  {/* Bottom navy scrim carries caption contrast over the photo. */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-brand-navy/70 via-brand-navy/15 to-transparent"
                  />

                  {/* Caption: slides up behind a gold hairline on hover; always
                      visible under reduced motion so nothing is gated on motion. */}
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                    <span className="block h-px w-10 origin-left scale-x-0 bg-brand-gold transition-transform duration-700 ease-out group-hover:scale-x-100 motion-reduce:scale-x-100 motion-reduce:transition-none" />
                    <p className="mt-3 translate-y-2 font-label text-xs tracking-[0.2em] text-white uppercase opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100 motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none">
                      {tile.caption}
                    </p>
                  </div>
                </MediaFrame>
              </TiltCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
